/**
 * Contract of `/api/messages` (Api::MessagesController) — private messages
 * between members. Who may write to whom is decided by the community's
 * communication rules (CommunityMessaging); the recipients endpoint already
 * applies them, and `POST /messages` answers 422 (`errors.recipient`) when a
 * recipient is not reachable.
 */

export type CommunityMessageView = 'inbox' | 'sent' | 'archive';
export const COMMUNITY_MESSAGE_VIEWS: readonly CommunityMessageView[] = ['inbox', 'sent', 'archive'];

export interface CommunityMessageUser {
  id: number;
  username: string | null;
  full_name: string | null;
  avatar_url: string | null;
}

export interface CommunityMessage {
  id: number;
  sender: CommunityMessageUser;
  recipient: CommunityMessageUser;
  subject: string;
  content: string;
  read_at: string | null;
  is_read: boolean;
  created_at: string;
  parent_id: number | null;
  /** Archived from the viewer's side (sender or recipient). */
  is_archived: boolean;
  /** Only on `GET /messages/:id`: the parent chain, oldest first. */
  thread?: CommunityMessage[];
}

export interface CommunityMessageListResponse {
  data: CommunityMessage[];
  total_count: number;
  page: number;
  per_page: number;
  total_pages: number;
  /** Unread in the inbox, regardless of the requested view. */
  unread_count: number;
}

export interface CommunityMessageRecipient {
  id: number;
  username: string | null;
  full_name: string | null;
  /** Admins only. */
  email?: string | null;
  avatar_url: string | null;
  is_sponsor: boolean;
  display_label: string;
}

export interface CommunityRecipientsResponse {
  recipients: CommunityMessageRecipient[];
  /** Admins get every user and may filter with `?search=`. */
  is_admin: boolean;
  /** True while a network-wide community write block also closes private messages. */
  messaging_muted?: boolean;
}

export interface CommunityMessagePayload {
  recipient_id: number;
  subject: string;
  content: string;
}
